import { useState } from "react";
import { Modal } from "react-bootstrap";
import { MdKeyboardArrowRight } from "react-icons/md";
import ThumbDown from "../../../icons/ThumbDown.svg?react";
import ThumbUp from "../../../icons/ThumbUp.svg?react";
import Responded from "../../../icons/Responded.svg?react";
import Info from "../../../icons/infoIcon.svg?react";
import AuditeeAccordion from "./AuditeeAccordion";
import styles from "../style.module.scss";

// Awaiting Response Icon (hourglass)
const AwaitingIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.8578 23.2155C18.1305 23.2155 23.2155 18.1304 23.2155 11.8577C23.2155 5.58504 18.1305 0.5 11.8578 0.5C5.58503 0.5 0.5 5.58504 0.5 11.8577C0.5 18.1304 5.58503 23.2155 11.8578 23.2155Z"
      fill="white"
      stroke="#D77D07"
      stroke-miterlimit="10"
    />
    <g clip-path="url(#clip0_2289_10960)">
      <path
        d="M16.6356 4.85779V6.80561L15.8711 6.86283C15.893 8.45518 15.8906 9.8284 14.5259 10.8741C14.2666 11.0726 12.8848 11.7361 12.8617 11.8286C12.8325 11.9467 12.897 11.9077 12.9408 11.9333C13.8247 12.475 14.7207 12.7538 15.3221 13.6887C15.9624 14.6846 15.8455 15.6767 15.8528 16.8101C15.9149 16.9988 16.4628 16.8771 16.6356 16.91V18.8578H7.0791V16.91L7.84362 16.8527C7.83145 15.342 7.85093 13.9176 9.12797 12.9023C9.39093 12.6929 10.8226 11.982 10.853 11.887C10.8883 11.7762 10.8092 11.8042 10.7763 11.7787C10.2954 11.4147 9.62832 11.2089 9.12919 10.8121C7.85215 9.79918 7.82658 8.37361 7.84362 6.86283L7.0791 6.80561V4.85779H16.6356Z"
        fill="#D77D07"
      />
      <rect
        x="7.45166"
        y="5.30896"
        width="8.81198"
        height="1.12109"
        fill="white"
      />
      <rect
        x="7.45166"
        y="17.3317"
        width="8.81198"
        height="1.12109"
        fill="white"
      />
      <path
        d="M14.3384 7.74426V8.04407C14.3384 9.41514 13.226 10.5265 11.855 10.5265C10.4841 10.5263 9.37256 9.41501 9.37256 8.04407V7.74426H14.3384Z"
        fill="#D77D07"
        stroke="#D77D07"
        stroke-width="0.971246"
      />
      <path
        d="M14.3384 16.1183V15.8185C14.3384 14.4474 13.226 13.3361 11.855 13.3361C10.4841 13.3363 9.37256 14.4475 9.37256 15.8185V16.1183H14.3384Z"
        fill="#D77D07"
        stroke="#D77D07"
        stroke-width="0.971246"
      />
    </g>
    <defs>
      <clipPath id="clip0_2289_10960">
        <rect
          width="9.55652"
          height="14"
          fill="white"
          transform="translate(7.0791 4.85779)"
        />
      </clipPath>
    </defs>
  </svg>
);

interface ConversationType {
  conversationId: number;
  askedBy: string;
  askedOn: string | null;
  question: string;
  response: string | null;
  complianceStatus: boolean | null;
  aiValidation: {
    satisfactory: string;
    followupQuestions: string[];
    reason: string;
  } | null;
  responseValidationRequired: boolean;
  reason: string | null;
  questionCode: string;
  documents: { fileName: string }[];
  respondedOn: string | null;
  responseValidatedOn: string | null;
  responseValidatedBy: any | null;
}

interface Auditee {
  name: string;
  conversations: ConversationType[];
}

interface Question {
  questionId: number;
  questionText: string;
  artifacts: string;
  isCustomQuestion: boolean;
  clauseId?: string;
  description?: string;
  purpose?: string;
  auditees: Auditee[];
}

interface QuestionAccordionProps {
  question: Question;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  openResponders: { [key: string]: boolean };
  onResponderToggle: (key: string) => void;
  columns: { label: string; key: string }[];
  responseValidationRequired?: boolean;
  isViewingPastSequence?: boolean;
}

const QuestionAccordion = ({
  question,
  index,
  isExpanded,
  onToggle,
  openResponders,
  onResponderToggle,
  columns,
  responseValidationRequired = false,
  isViewingPastSequence = false,
}: QuestionAccordionProps) => {
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Check if any auditee has responded (has a response)
  const hasAnyResponded = question.auditees?.some((auditee) =>
    auditee.conversations?.some((conv) => conv.response !== null),
  );

  // Check if any auditee has a determined compliance status
  const hasAnyComplianceStatus = question.auditees?.some((auditee) =>
    auditee.conversations?.some(
      (conv) =>
        conv.complianceStatus !== null && conv.complianceStatus !== undefined,
    ),
  );

  // Determine overall question status from auditees
  const hasAnySatisfied = question.auditees?.some((auditee) =>
    auditee.conversations?.some((conv) => conv.complianceStatus === true),
  );
  const hasAnyNotSatisfied = question.auditees?.some((auditee) =>
    auditee.conversations?.some(
      (conv) => conv.response && conv.complianceStatus === false,
    ),
  );

  // Determine which icon to show
  const renderStatusIcon = () => {
    // If no response yet, show awaiting
    if (!hasAnyResponded) return <AwaitingIcon />;

    // If responseValidationRequired and no compliance status determined yet, show Responded
    if (responseValidationRequired && !hasAnyComplianceStatus) {
      return <Responded />;
    }

    // Show thumbs up/down based on compliance status
    if (hasAnySatisfied) return <ThumbUp />;
    if (hasAnyNotSatisfied) return <ThumbDown />;

    return <AwaitingIcon />;
  };

  return (
    <div className={styles.questionRow}>
      {/* Question Header */}
      <div
        className={styles.questionHeader}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
        onClick={onToggle}
      >
        <MdKeyboardArrowRight
          size={22}
          className={`${styles.questionArrow} ${isExpanded ? styles.questionArrowExpanded : ""
            }`}
        />
        <div className={styles.questionContent}>
          <div className={styles.questionText}>
            <span className={styles.questionNumber}>{index + 1}.</span>
            {question.questionText}
            {/* Info icon */}
            <span className={styles.infoIconWrapper}>
              <Info
                className={styles.iconStyleActions}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowInfoModal(true);
                }}
              />
            </span>
          </div>
        </div>
        <div className={styles.questionStatusIcon}>{renderStatusIcon()}</div>
      </div>

      {/* Auditee Accordions */}
      {isExpanded && (
        <div className={styles.auditeeAccordion}>
          {question.auditees?.map((auditee) => {
            const responderKey = `${question.questionId}-${auditee.name}`;
            return (
              <AuditeeAccordion
                key={auditee.name}
                auditee={auditee}
                questionId={question.questionId}
                isOpen={openResponders[responderKey] || false}
                onToggle={() => onResponderToggle(responderKey)}
                columns={columns}
                responseValidationRequired={responseValidationRequired}
                isViewingPastSequence={isViewingPastSequence}
              />
            );
          })}
        </div>
      )}

      {/* Question Description Modal */}
      <Modal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        centered
        className={styles.questionInfoModal}
        size="lg"
      >
        <Modal.Header closeButton className={styles.questionInfoModalHeader}>
          <Modal.Title style={{ color: "white" }}>
            Question Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.questionInfoModalBody}>
          {question.clauseId && (
            <div className={styles.infoModalRow}>
              <span className={styles.infoModalLabel}>Clause ID:</span>
              <span className={styles.infoModalValue}>{question.clauseId}</span>
            </div>
          )}
          {question.description && (
            <div className={styles.infoModalRow}>
              <span className={styles.infoModalLabel}>Description:</span>
              <span className={styles.infoModalValue}>
                {question.description}
              </span>
            </div>
          )}
          {question.questionText && (
            <div className={styles.infoModalRow}>
              <span className={styles.infoModalLabel}>Question:</span>
              <span className={styles.infoModalValue}>
                {question.questionText}
              </span>
            </div>
          )}
          {question.purpose && (
            <div className={styles.infoModalRow}>
              <span className={styles.infoModalLabel}>Purpose:</span>
              <span className={styles.infoModalValue}>{question.purpose}</span>
            </div>
          )}
          {question.artifacts && (
            <div className={styles.infoModalRow}>
              <span className={styles.infoModalLabel}>Artifacts:</span>
              <span className={styles.infoModalValue}>
                {question.artifacts}
              </span>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default QuestionAccordion;
